import AnimateOnScroll from './AnimateOnScroll';
const benefits = [
  {
    icon: 'ri-bar-chart-grouped-line',
    title: 'Modernise Prospecting Process',
    desc: 'Draw in prospects with a modern, engaging planning experience that feels personalised and modernised.',
  },
  {
    icon: 'ri-flow-chart',
    title: 'Digitise Cash Flow Planning Analyses',
    desc: 'Reclaim time by automating labour-intensive risk analysis and planning workflows, boosting efficiency and capacity.',
  },
  {
    icon: 'ri-scales-3-line',
    title: 'Create Realistic Forecasts',
    desc: 'We don\'t just get the maths right — we model your client’s goals and aspirations. Our plans reflect realistic, achievable outcomes, so clients trust them, engage with them, and follow through.',
  },
  {
    icon: 'ri-file-paper-2-line',
    title: 'Generate Custom Reports Instantly',
    desc: 'Generate polished, client-ready planning reports at the flick of a button — branded with your firm\'s logo and colours, laid out so clients actually understand them.',
  },
  {
    icon: 'ri-user-heart-line',
    title: 'Serve, Retain & Engage Clients',
    desc: 'Enhance loyalty with optimised, consistent plans that provide certainty in an uncertain digital landscape.',
  },
];

export default function BenefitsSection() {
  return (
    <section id="benefits" className="bg-[#EEEAE0] py-24">
      <div className="max-w-6xl mx-auto px-6">
        <AnimateOnScroll variant="fade-up">
          <div className="text-center mb-16">
            <p className="text-xs font-bold tracking-widest text-brown-300 uppercase mb-4">Benefits</p>
            <h2 className="text-4xl lg:text-5xl font-bold text-[#1a1a2e]">Less Admin. More Time. Happier Clients.</h2>
          </div>
        </AnimateOnScroll>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <AnimateOnScroll variant="fade-left" delay={100}>
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-lg border border-[#EDE8DF] overflow-hidden">
                <div className="bg-[#f9f6f0] border-b border-[#EDE8DF] px-5 py-3 flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded bg-[#E0D9CE]"></div>
                  <div className="w-2.5 h-2.5 rounded bg-[#E0D9CE]"></div>
                  <div className="w-2.5 h-2.5 rounded bg-[#E0D9CE]"></div>
                  <span className="ml-3 text-xs text-[#9a9aaa] font-medium">Your planning report</span>
                </div>
                <div className="p-5">
                  {/* Planning report mockup — styled after Envizage report */}
                  <div className="w-full bg-white rounded overflow-hidden border border-[#E8F5EE]">

                    {/* Report header */}
                    <div className="px-4 pt-3 pb-2 flex items-start justify-between border-b border-[#E8F5EE]">
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className="ri-leaf-line text-base text-[#1BB184]"></i>
                      </div>
                      <div className="text-right">
                        <p className="text-md font-bold text-[#1a1a2e]">Your planning report</p>
                        <p className="text-[10px] text-[#00CC7A] font-medium">March 25th 2026</p>
                      </div>
                    </div>

                    {/* Section 01 */}
                    <div className="px-4 pt-3">
                      <div className="flex items-center gap-2 mb-2.5">
                        <span className="text-xl font-extrabold text-[#00CC7A] leading-none">01</span>
                        <span className="text-xs font-bold text-[#1a1a2e]">What you told us</span>
                      </div>

                      {/* About */}
                      <p className="text-[10px] font-semibold text-[#00CC7A] mb-1.5">About</p>
                      <div className="flex gap-2 mb-3">
                        <div className="flex-1 border border-[#E8F5EE] rounded p-2">
                          <p className="text-[8px] text-[#9a9aaa] uppercase tracking-wider mb-0.5">ME</p>
                          <p className="text-xs font-semibold text-[#1a1a2e]">Sarah Thompson</p>
                          <p className="text-[10px] text-[#9a9aaa]">42 years old</p>
                        </div>
                        <div className="flex-1 border border-[#E8F5EE] rounded p-2">
                          <div className="flex items-center gap-1 mb-0.5">
                            <div className="w-3 h-3 rounded bg-[#00CC7A] flex items-center justify-center flex-shrink-0">
                              <span className="text-[7px] text-white font-bold leading-none">P</span>
                            </div>
                            <p className="text-[8px] text-[#9a9aaa] uppercase tracking-wider">PARTNER</p>
                          </div>
                          <p className="text-xs font-semibold text-[#1a1a2e]">James Thompson</p>
                          <p className="text-[10px] text-[#9a9aaa]">44 years old</p>
                        </div>
                      </div>

                      {/* Goals */}
                      <p className="text-[10px] font-semibold text-[#00CC7A] mb-1.5">Goals</p>
                      <div className="flex gap-1.5 mb-3">
                        {[
                          { label: 'Retire at 67', val: '£480K' },
                          { label: 'Clear mortgage', val: '£200K' },
                          { label: "Child's uni", val: '£60K' },
                          { label: 'Holiday fund', val: '£20K' },
                        ].map((g) => (
                          <div key={g.label} className="flex-1 border border-[#E8F5EE] rounded p-1.5">
                            <p className="text-[8px] text-[#9a9aaa] uppercase tracking-wide leading-tight mb-0.5">GOAL</p>
                            <p className="text-[9px] font-semibold text-[#1a1a2e] leading-tight">{g.label}</p>
                            <p className="text-[9px] text-[#00CC7A] font-bold mt-0.5">{g.val}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* What you earn */}
                    <div className="px-4 pb-4 border-t border-[#E8F5EE] pt-3 flex items-start gap-3">
                      {/* Donut chart */}
                      <div className="flex-shrink-0">
                        <p className="text-[10px] font-semibold text-[#00CC7A] mb-2">What you earn</p>
                        <div className="relative w-14 h-14">
                          <div
                            className="w-14 h-14 rounded-full"
                            style={{ background: 'conic-gradient(#00CC7A 0% 55%, #1BB184 55% 80%, #7FEBB8 80% 100%)' }}
                          ></div>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-8 h-8 rounded-full bg-white"></div>
                          </div>
                        </div>
                      </div>

                      {/* Breakdown */}
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-end mb-2">
                          <div className="text-right">
                            <p className="text-base font-extrabold text-[#1a1a2e] leading-none">£6,000</p>
                            <p className="text-[9px] text-[#9a9aaa]">per month</p>
                          </div>
                        </div>
                        {[
                          { label: 'Salary', dot: '#00CC7A', val: '£3,300' },
                          { label: 'Rental income', dot: '#1BB184', val: '£1,500' },
                          { label: 'Other income', dot: '#7FEBB8', val: '£1,200' },
                        ].map((r) => (
                          <div key={r.label} className="flex items-center justify-between py-0.5">
                            <div className="flex items-center gap-1.5">
                              <div className="w-2 h-2 rounded flex-shrink-0" style={{ background: r.dot }}></div>
                              <span className="text-[10px] text-[#6a6a7a]">{r.label}</span>
                            </div>
                            <span className="text-[10px] font-bold text-[#1a1a2e]">{r.val}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-3 gap-3">
                    {[
                      { label: 'Retirement Age', val: '67' },
                      { label: 'Monthly Savings', val: '£2,400' },
                      { label: 'Projected Fund', val: '£840K' },
                    ].map((item) => (
                      <div key={item.label} className="bg-[#EFEBE5] rounded p-3 text-center">
                        <p className="text-xs text-[#9a9aaa] mb-1">{item.label}</p>
                        <p className="text-md font-bold text-[#1a1a2e]">{item.val}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </AnimateOnScroll>

          <div className="flex flex-col gap-6">
            {benefits.map((b, i) => (
              <AnimateOnScroll key={b.title} variant="fade-right" delay={i * 100}>
                <div className="flex items-start gap-4 group">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white border border-[#EDE8DF] flex-shrink-0 group-hover:border-[#1a1a2e] transition-colors">
                    <i className={`${b.icon} text-lg text-[#1a1a2e]`}></i>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-[#1a1a2e] mb-1">{b.title}</h3>
                    <p className="text-md text-[#6a6a7a] leading-relaxed">{b.desc}</p>
                  </div>
                </div>
              </AnimateOnScroll>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
